import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { lotesAPI } from '../lib/api';
import { formatCurrency, formatNumber, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Bird,
  Plus,
  ArrowRight,
  Calendar,
  CurrencyDollar,
  TrendUp,
  TrendDown,
} from '@phosphor-icons/react';

export default function LotesList() {
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadLotes();
  }, []);

  const loadLotes = async () => {
    try {
      const response = await lotesAPI.getAll();
      setLotes(response.data);
    } catch (error) {
      toast.error('Error al cargar los lotes');
    } finally {
      setLoading(false);
    }
  };

  const filteredLotes = lotes.filter((lote) => {
    if (filter === 'all') return true;
    return lote.estado === filter;
  });

  const activosCount = lotes.filter((l) => l.estado === 'activo').length;
  const cerradosCount = lotes.filter((l) => l.estado === 'cerrado').length;

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <Skeleton className="h-10 w-48" />
            <Skeleton className="h-10 w-32" />
          </div>
          <Skeleton className="h-12 w-full" />
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="lotes-list">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Mis Lotes
            </h1>
            <p className="text-muted-foreground">
              Gestiona todos tus lotes de pollos de engorde
            </p>
          </div>
          <Link to="/lotes/nuevo">
            <Button className="bg-[#E73425] hover:bg-[#D81C23] btn-animate" data-testid="create-lote-btn">
              <Plus size={18} className="mr-2" />
              Nuevo Lote
            </Button>
          </Link>
        </div>

        {/* Tabs Filter */}
        <Tabs defaultValue="all" onValueChange={setFilter}>
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="all" data-testid="tab-all">
              Todos ({lotes.length})
            </TabsTrigger>
            <TabsTrigger value="activo" data-testid="tab-activo">
              Activos ({activosCount})
            </TabsTrigger>
            <TabsTrigger value="cerrado" data-testid="tab-cerrado">
              Cerrados ({cerradosCount})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Lotes Grid */}
        {filteredLotes.length > 0 ? (
          <div className="grid gap-4">
            {filteredLotes.map((lote) => (
              <Card
                key={lote.id}
                className="border-border/60 hover:border-primary/50 hover:shadow-md transition-all"
                data-testid={`lote-item-${lote.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Left - Main Info */}
                    <div className="flex items-start gap-4">
                      <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                        lote.estado === 'activo' ? 'bg-emerald-100' : 'bg-slate-100'
                      }`}>
                        <Bird
                          size={24}
                          weight="fill"
                          className={lote.estado === 'activo' ? 'text-emerald-600' : 'text-slate-600'}
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold">{lote.nombre}</h3>
                          <Badge
                            variant="outline"
                            className={lote.estado === 'activo' ? 'status-activo' : 'status-cerrado'}
                          >
                            {lote.estado === 'activo' ? 'Activo' : 'Cerrado'}
                          </Badge>
                          {lote.estado === 'activo' && (
                            <Badge
                              variant="outline"
                              className={
                                lote.dias_transcurridos >= 60
                                  ? 'alert-critical'
                                  : lote.dias_transcurridos >= 45
                                  ? 'alert-warning'
                                  : lote.dias_transcurridos >= 40
                                  ? 'alert-info'
                                  : ''
                              }
                            >
                              Día {lote.dias_transcurridos}
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Bird size={14} />
                            {formatNumber(lote.cantidad_pollitos)} pollitos
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            Inicio: {formatDate(lote.fecha_inicio)}
                          </span>
                          {lote.estado === 'cerrado' && lote.fecha_venta && (
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              Venta: {formatDate(lote.fecha_venta)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right - Financial Info */}
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Inversión</p>
                        <p className="text-xl font-bold text-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {formatCurrency(lote.total_inversion)}
                        </p>
                      </div>

                      {lote.estado === 'cerrado' && lote.ganancia_neta !== null && (
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">
                            {lote.ganancia_neta >= 0 ? 'Ganancia' : 'Pérdida'}
                          </p>
                          <p
                            className={`text-xl font-bold flex items-center justify-end gap-1 ${
                              lote.ganancia_neta >= 0 ? 'text-emerald-600' : 'text-red-500'
                            }`}
                            style={{ fontFamily: 'JetBrains Mono, monospace' }}
                          >
                            {lote.ganancia_neta >= 0 ? (
                              <TrendUp size={18} weight="bold" />
                            ) : (
                              <TrendDown size={18} weight="bold" />
                            )}
                            {formatCurrency(Math.abs(lote.ganancia_neta))}
                          </p>
                        </div>
                      )}

                      <Link to={`/lotes/${lote.id}`}>
                        <Button variant="outline" size="sm" className="btn-animate" data-testid={`view-lote-${lote.id}`}>
                          Ver <ArrowRight size={16} className="ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Alerts */}
                  {lote.alertas && lote.alertas.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-border/60">
                      {lote.alertas.map((alerta, idx) => (
                        <p key={idx} className="text-sm text-orange-600 font-medium">
                          ⚠️ {alerta}
                        </p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-border/60">
            <CardContent className="py-12">
              <div className="empty-state">
                <Bird size={64} className="text-muted-foreground/50 mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  {filter === 'all'
                    ? 'No hay lotes registrados'
                    : filter === 'activo'
                    ? 'No hay lotes activos'
                    : 'No hay lotes cerrados'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {filter === 'all' || filter === 'activo'
                    ? 'Comience creando su primer lote de pollos de engorde'
                    : 'Los lotes cerrados aparecerán aquí'}
                </p>
                {(filter === 'all' || filter === 'activo') && (
                  <Link to="/lotes/nuevo">
                    <Button className="bg-[#E73425] hover:bg-[#D81C23]">
                      <Plus size={18} className="mr-2" />
                      Crear Lote
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
